/**
 * localized-text — the locale-keyed sibling of an operator's own typed
 * string, and the one function every reader resolves the pair through.
 *
 * The same override-or-default job `legal.ts`'s `resolveLegalDocument`
 * does, generalized to a single free-text field: the operator's own string
 * stays the source of record and the map is additive, so a subject carrying
 * no map renders exactly as its source does. A field that carries a source
 * string is read through `resolveLocalizedText`, never keyed at a template,
 * so the fallback ladder is one decision rather than one per surface.
 * Synchronous, no I/O, no DOM:
 * a string in, a string out, and the caller interpolates it as text (never
 * as HTML — the map's values are operator- and model-authored prose).
 *
 * `localizedText` is a factory rather than one constant because the value
 * a map carries is the field's own, cap and nullability included: a ticket
 * type's description is bounded at 200 characters in every language and an
 * event's description is bounded in none. The key is always `LocaleEnum`, so a
 * locale this deployment does not serve cannot be written to any of them.
 *
 * A `z.partialRecord` over a finite key type infers a PARTIAL map — `z.infer` is
 * exactly `Partial<Record<"de" | "en", …>>` — so one locale alone is a legal
 * value and no construction site is forced to supply both. That inference is
 * what lets an operator write one language and leave the other for later, so
 * `localized-text.test.ts` pins it with a type-equality assertion that
 * accepts `Partial<Record<…>>` and rejects `Record<…>`.
 *
 * A map is cleared by writing the whole field null, never by nulling one
 * locale's entry: an entry is a string or it is not there.
 */
import { z } from "zod";
import { type Locale, LocaleEnum } from "./translate.ts";

export function localizedText<T extends z.ZodTypeAny>(value: T) {
	return z.partialRecord(LocaleEnum, value);
}

export const LocalizedTextSchema = localizedText(z.string().min(1));
export type LocalizedText = z.infer<typeof LocalizedTextSchema>;

/**
 * The text a reader in `locale` actually sees: that locale's own entry when
 * the map carries one, the operator's own source string otherwise, and null
 * when there is neither. Null rather than an empty string, so a caller's
 * `if (!text)` and a `?? fallback` both read the absence the same way.
 *
 * A source that cannot be absent resolves to a text that cannot be absent —
 * the overload is what lets a required field (a ticket type's own name,
 * frozen onto every ticket a delivery mints) stay required through the
 * resolution rather than needing an assertion on the far side of it.
 */
export function resolveLocalizedText(
	byLocale: LocalizedText | null | undefined,
	source: string,
	locale: Locale,
): string;
export function resolveLocalizedText(
	byLocale: LocalizedText | null | undefined,
	source: string | null | undefined,
	locale: Locale,
): string | null;
export function resolveLocalizedText(
	byLocale: LocalizedText | null | undefined,
	source: string | null | undefined,
	locale: Locale,
): string | null {
	return byLocale?.[locale] ?? source ?? null;
}
